
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import PDFTemplateUploader from '@/components/student-certification/certificate-templates/PDFTemplateUploader';

const CreateTemplateUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [pdfTemplate, setPdfTemplate] = useState<{ file: File, url: string } | null>(null);
  const [basicInfo, setBasicInfo] = useState<any>(null);

  useEffect(() => {
    // Retrieve basic info from session storage
    const storedBasicInfo = sessionStorage.getItem('certificateTemplateBasicInfo');
    if (!storedBasicInfo) {
      // If no basic info is found, redirect back to the first step
      toast.error('Please complete the basic information first');
      navigate('/certificate-templates/new/basic-info');
      return;
    }
    
    setBasicInfo(JSON.parse(storedBasicInfo));
    
    // Check if we already have PDF data stored
    const storedPdfData = sessionStorage.getItem('certificateTemplatePdfData');
    if (storedPdfData) {
      try {
        const pdfData = JSON.parse(storedPdfData);
        // We can't restore the File object, but we can restore the URL
        setPdfTemplate({
          file: new File([], pdfData.fileName || 'template.pdf'), // Placeholder
          url: pdfData.url
        });
      } catch (error) {
        console.error('Error parsing stored PDF data:', error);
      }
    }
  }, [navigate]);

  const handlePdfUploaded = (file: File, previewUrl: string) => {
    setPdfTemplate({ file, url: previewUrl });
    
    // Store in session storage
    sessionStorage.setItem('certificateTemplatePdfData', JSON.stringify({
      fileName: file.name,
      url: previewUrl,
      size: file.size
    }));
    
    toast.success('PDF template uploaded successfully');
  };

  const handlePrevious = () => {
    navigate('/certificate-templates/new/basic-info');
  };

  const handleNext = () => {
    if (!pdfTemplate) {
      // Allow skipping PDF upload
      const proceed = window.confirm('Do you want to proceed without uploading a PDF template?');
      if (!proceed) return;
    }
    
    navigate('/certificate-templates/new/mapping');
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Create Certificate Template - Upload PDF | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Create Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 2: Upload a PDF template (optional)
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              2
            </div>
            <span className="text-sm mt-1 text-primary font-medium">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              3
            </div>
            <span className="text-sm mt-1">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Upload PDF Template</CardTitle>
          <CardDescription>
            Upload a PDF file to use as the base for your certificate. You can add dynamic text fields in the next step.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pdfTemplate ? (
            <div className="border rounded-md p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="font-medium">{pdfTemplate.file.name || 'Certificate Template'}</p>
                    <p className="text-sm text-muted-foreground">
                      {pdfTemplate.file.size > 0 ? `${(pdfTemplate.file.size / 1024).toFixed(1)} KB` : ''}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    setPdfTemplate(null);
                    sessionStorage.removeItem('certificateTemplatePdfData');
                  }}
                >
                  Remove
                </Button>
              </div>
              <div className="h-[500px] border rounded overflow-hidden">
                <iframe
                  src={`${pdfTemplate.url}#toolbar=0`}
                  className="w-full h-full"
                  title="PDF Certificate Template"
                />
              </div>
            </div>
          ) : (
            <PDFTemplateUploader onPDFUploaded={handlePdfUploaded} />
          )}
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button type="button" onClick={handleNext}>
              {pdfTemplate ? 'Continue' : 'Skip'} <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTemplateUploadPage;
